import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { RaptorLogo } from '@/components/RaptorLogo';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { toast } from 'sonner';
import { Check, X, LogOut, Eye, EyeOff } from 'lucide-react';

// ---------------------------------------------------------------------------
// Password policy — must stay in sync with backend security.check_password_policy.
// We evaluate each rule client-side so the user gets live feedback instead
// of trial-and-error toasts. The backend still enforces authoritatively.
// ---------------------------------------------------------------------------
interface PolicyCheck {
  id: string;
  label: string;
  test: (pw: string) => boolean;
}

const PASSWORD_POLICY: PolicyCheck[] = [
  { id: 'len', label: 'At least 12 characters', test: (pw) => pw.length >= 12 },
  { id: 'upper', label: 'One uppercase letter (A–Z)', test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lower', label: 'One lowercase letter (a–z)', test: (pw) => /[a-z]/.test(pw) },
  { id: 'digit', label: 'One digit (0–9)', test: (pw) => /[0-9]/.test(pw) },
  { id: 'symbol', label: 'One symbol (! @ # $ etc.)', test: (pw) => /[!-/:-@[-`{-~]/.test(pw) },
];

const PolicyChecklist: React.FC<{ pw: string; confirm: string }> = ({ pw, confirm }) => {
  const results = useMemo(() => PASSWORD_POLICY.map((rule) => ({
    ...rule,
    ok: rule.test(pw),
  })), [pw]);
  const matches = pw.length > 0 && pw === confirm;

  return (
    <ul className="text-xs font-body space-y-0.5" aria-label="Password requirements" data-testid="password-policy-checklist">
      {results.map((r) => (
        <li key={r.id} className={`flex items-center gap-1.5 ${r.ok ? 'text-emerald-700' : 'text-raptor-navy/50'}`}>
          {r.ok ? <Check size={12} className="shrink-0" /> : <X size={12} className="shrink-0" />}
          <span>{r.label}</span>
        </li>
      ))}
      <li className={`flex items-center gap-1.5 pt-1 border-t border-border/40 mt-1 ${matches ? 'text-emerald-700' : 'text-raptor-navy/50'}`}>
        {matches ? <Check size={12} className="shrink-0" /> : <X size={12} className="shrink-0" />}
        <span>Both new-password fields match</span>
      </li>
    </ul>
  );
};

export const ForceResetPage: React.FC = () => {
  const { user, logout, setForceResetDone } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  if (!user) { nav('/login', { replace: true }); return null; }

  const allRulesPass = PASSWORD_POLICY.every((r) => r.test(next)) && next === confirm && next !== current;

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setServerError(null);
    if (next !== confirm) { setServerError('The new-password fields do not match.'); return; }
    if (next === current) { setServerError('New password must differ from the temporary password.'); return; }
    const failing = PASSWORD_POLICY.find((r) => !r.test(next));
    if (failing) { setServerError(failing.label + ' — required.'); return; }

    setBusy(true);
    try {
      await api.post('/auth/force-reset', { current_password: current, new_password: next });
      setForceResetDone();
      toast.success('Password updated.');
      nav(user.context === 'broker' ? '/broker' : user.context === 'master' ? '/master' : '/insurer', { replace: true });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Reset failed';
      setServerError(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const signOut = async (): Promise<void> => {
    try { await logout(); } catch { /* logout swallows */ }
    nav('/login', { replace: true });
  };

  return (
    <div className="flex-1 flex items-center justify-center p-6" data-testid="force-reset-page">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="mb-3"><RaptorLogo /></div>
          <CardTitle className="text-lg">Set a new password</CardTitle>
          <CardDescription>
            Signed in as <b>{user.email}</b>. You must change your temporary password before continuing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Temporary password</Label>
              <div className="relative">
                <Input
                  data-testid="reset-current"
                  type={showCurrent ? 'text' : 'password'}
                  required
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  className="pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  aria-label={showCurrent ? 'Hide' : 'Show'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-raptor-navy/40 hover:text-raptor-blue"
                >
                  {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>New password</Label>
              <div className="relative">
                <Input
                  data-testid="reset-new"
                  type={showNext ? 'text' : 'password'}
                  required
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  className="pr-9"
                />
                <button
                  type="button"
                  onClick={() => setShowNext((v) => !v)}
                  aria-label={showNext ? 'Hide' : 'Show'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-raptor-navy/40 hover:text-raptor-blue"
                >
                  {showNext ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Confirm new password</Label>
              <Input
                data-testid="reset-confirm"
                type={showNext ? 'text' : 'password'}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>

            <div className="rounded-md bg-raptor-neutral/40 border border-border/60 p-2">
              <PolicyChecklist pw={next} confirm={confirm} />
            </div>

            {serverError && (
              <div
                className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900 font-body"
                data-testid="reset-error"
              >
                {serverError}
              </div>
            )}

            <Button
              data-testid="reset-submit"
              type="submit"
              variant="gradient"
              className="w-full"
              disabled={busy || !allRulesPass || !current}
            >
              {busy ? 'Updating…' : 'Update password'}
            </Button>

            {/* Escape hatch — always available so users are never trapped. */}
            <div className="flex items-center justify-between pt-2 border-t border-border/60 text-xs font-body">
              <span className="text-raptor-navy/60">Locked out?</span>
              <button
                type="button"
                onClick={() => void signOut()}
                className="inline-flex items-center gap-1 text-raptor-blue hover:underline"
                data-testid="reset-signout"
              >
                <LogOut size={12} /> Sign out and try another account
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
